/*
 * -----------------------------------------------------------------------
 *  <copyright file="grid-view-menu/grid-view-menu.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Grow from '@mui/material/Grow';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import cn from 'clsx';
import React from 'react';
import { useTranslation } from '@hooks/index';
import './grid-view-menu.scss';
import DarkTooltip from '../../../dark-tool-tip/dark-tool-tip';
import { IGridViewMenuProps, IGridViewMenuItem } from './grid-view-menu-props.interface';

const GridViewMenu = ({
    viewOptions,
    gridKey,
    transformOrigin,
    anchorOrigin,
    height,
    wrapperClassName,
    menuClassName,
}: IGridViewMenuProps) => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const currentView = viewOptions.find((viewOption) => viewOption.isActive);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleMenuItemClick = (item: IGridViewMenuItem) => {
        item.onClick();
        handleClose();
    };

    return (
        <div className={cn('menu-wrapper-v2', wrapperClassName, { opened: anchorEl })}>
            <DarkTooltip title={t(currentView.label)} arrow placement="top">
                <Button
                    aria-controls="actions-menu"
                    aria-haspopup="false"
                    aria-describedby="actions-menu"
                    className="btn-view"
                    style={{ height }}
                    onClick={handleClick}
                    data-testid="grid-layout-menu"
                >
                    {currentView.customIcon ?? (
                        <FontAwesomeIcon icon={currentView.icon} size="lg" className="view-icon" />
                    )}
                </Button>
            </DarkTooltip>
            <Menu
                id="actions-menu"
                className={cn('menu-items-v2', menuClassName)}
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slots={{ transition: Grow }}
                anchorOrigin={{ vertical: anchorOrigin.vertical, horizontal: anchorOrigin.horizontal }}
                transformOrigin={{ vertical: transformOrigin.vertical, horizontal: transformOrigin.horizontal }}
            >
                {viewOptions.map((item: any) => (
                    <MenuItem
                        className={cn('menu-item', { disabled: !item.enabled })}
                        key={t(item.label)}
                        onClick={() => handleMenuItemClick(item)}
                    >
                        <div className="menu-icon-wrap">
                            {item.customIcon}
                            {!item.customIcon && item.icon && (
                                <FontAwesomeIcon icon={[item.icon[0], item.icon[1]]} className="menu-icon" />
                            )}
                        </div>
                        <Typography variant="inherit" className="label-menu">
                            {t(item.label)}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default GridViewMenu;
